import { Link } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faCheckDouble,
  faHandshake,
  faMapLocationDot,
  faSearch,
  faShieldHalved,
  faUserGroup
} from "@fortawesome/free-solid-svg-icons";
import Seo from "../components/Seo";
import { PAGE_SEO } from "../seo/site";

const DISCOVER_ITEMS = [
  {
    icon: faShieldHalved,
    title: "RERA Project Information",
    text: "Find available RERA registration numbers and project-related information to help investigate a project's registration status and applicable disclosures."
  },
  {
    icon: faCheckDouble,
    title: "HMDA and DTCP Approval References",
    text: "Discover available planning authority details, layout approval references, LP numbers, and related project information. Use these details to seek confirmation from the relevant authority."
  },
  {
    icon: faBuilding,
    title: "Venture and Project Details",
    text: "Explore available project names, developer or promoter information, application numbers, project categories, locations, and other listed particulars."
  },
  {
    icon: faMapLocationDot,
    title: "Location-Based Venture Discovery",
    text: "Search for ventures in locations of interest and explore available information to help narrow your property search."
  },
  {
    icon: faHandshake,
    title: "Developer Digital Visibility",
    text: "Help prospective buyers and real estate professionals discover your ventures through organized project listings and potential featured promotional opportunities."
  },
  {
    icon: faSearch,
    title: "Smarter Property Research",
    text: "Use a searchable directory to identify projects of interest, compare available information, and prepare better questions before contacting a developer or visiting a site."
  }
];

const AUDIENCE = [
  { title: "Buyers", text: "Discover ventures and investigate available project information." },
  { title: "Investors", text: "Explore projects and identify the details requiring further due diligence." },
  { title: "Real Estate Professionals", text: "Access venture information to support your property research and client conversations." },
  { title: "Developers", text: "Explore listing, brand visibility, and promotional partnership opportunities." }
];

const QUESTIONS = [
  "Is the project required to be registered with RERA, and if so, what is its registration status?",
  "Is the claimed HMDA or DTCP approval supported by the relevant official records?",
  "Is there a verifiable layout permission or LP/plan approval number?",
  "Does the project location match the land and venture being advertised?",
  "Are the developer, survey numbers, plot details, and other available project particulars consistent?",
  "Are there restrictions, pending permissions, ownership issues, or other risks that require independent investigation?"
];

function AboutPage() {
  return <main className="vd-page">
      <Seo {...PAGE_SEO.about} />
      <section className="vd-page-hero">
        <div className="vd-page-hero-inner">
          <p className="vd-page-kicker">About Us</p>
          <h1>Bringing Transparency to Real Estate Venture Discovery</h1>
          <p className="vd-page-lead">
            Discover Ventures. Verify Information. Invest with Greater Confidence.
          </p>
          <p>
            VentureDirectory.com is India’s emerging digital platform for real estate venture discovery—
            helping buyers, investors, agents, and developers find ventures, check approval details,
            discover locations, and connect with developers across Telangana.
          </p>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-grid two">
          <article className="vd-page-card">
            <h2>Who We Are</h2>
            <p>
              VentureDirectory.com is building a comprehensive digital directory of real estate ventures,
              plots, residential layouts, apartments, villas, and property development projects across Telangana.
            </p>
            <p>
              Our mission is to make essential real estate project information easier to discover, access,
              understand, and share—helping property buyers, investors, real estate agents, and developers
              make more informed decisions.
            </p>
            <p>
              From RERA registration numbers and HMDA/DTCP approval references to layout plan numbers,
              application IDs, project locations, developer details, and other available project information,
              VentureDirectory brings important venture-related data together in one searchable platform.
            </p>
          </article>

          <article className="vd-page-card accent">
            <h2>Why We Exist</h2>
            <p>
              Buying a plot, investing in a residential layout, or choosing a real estate project can be one
              of the most significant financial decisions in a person’s life. Yet finding and understanding
              reliable information about a venture is often difficult.
            </p>
            <p>
              Property buyers frequently depend on brochures, WhatsApp messages, promotional advertisements,
              and verbal assurances. Important details—including RERA registration, layout approval numbers,
              HMDA or DTCP approval references, and exact project locations—may not be readily accessible.
            </p>
            <p className="vd-page-emphasis">
              Because buying land is not just about finding the right location. It is about understanding what you are buying.
            </p>
          </article>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-section-head">
          <h2>The Problem We Want to Solve</h2>
          <p>Information gaps in real estate can expose buyers to financial risks, misleading claims, and costly mistakes.</p>
        </div>
        <div className="vd-page-card">
          <p>
            A beautiful brochure does not establish legal validity. A persuasive sales pitch does not prove that a
            layout has the required approvals. An advertised project location does not, by itself, confirm the
            identity or boundaries of the land being offered.
          </p>
          <p>Before purchasing a property, buyers may need to investigate questions such as:</p>
          <ul className="vd-page-list">
            {QUESTIONS.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p>
            VentureDirectory aims to put information discovery before investment decisions—not after a problem arises.
            Our platform helps users identify relevant project details and approval references so they can check the
            appropriate official records and obtain independent legal advice where necessary.
          </p>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-grid two">
          <article className="vd-page-card">
            <h2>Our Mission</h2>
            <p>
              To make real estate venture information more accessible, discoverable, and understandable—
              empowering property buyers and investors to ask the right questions, investigate important details,
              and make better-informed decisions.
            </p>
            <ul className="vd-page-list">
              <li>Build a comprehensive, searchable directory of real estate ventures across Telangana.</li>
              <li>Improve the discoverability of RERA registration details and HMDA/DTCP approval references where available.</li>
              <li>Organize project information, application numbers, plan approval references, locations, and developer details in one convenient platform.</li>
              <li>Encourage property buyers to verify material claims before investing.</li>
              <li>Help real estate developers improve their digital visibility and present project information more effectively.</li>
              <li>Support real estate agents and property professionals with better access to venture-related information.</li>
            </ul>
          </article>

          <article className="vd-page-card">
            <h2>Our Vision</h2>
            <p>
              To become Telangana’s trusted digital destination for real estate venture discovery—building a future
              where every property buyer knows what to investigate before investing.
            </p>
            <p>
              We envision a real estate ecosystem in which essential project information is easier to discover,
              approval claims are easier to investigate, developers can establish a stronger digital presence,
              and buyers are better equipped to make informed choices.
            </p>
            <p>
              Starting with Telangana, our long-term vision is to expand venture discovery and information access across India.
              We believe that a more informed property market benefits everyone: buyers, investors, genuine developers,
              real estate professionals, and communities.
            </p>
          </article>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-section-head">
          <h2>What You Can Discover</h2>
          <p>Essential venture information organized to support discovery and further investigation.</p>
        </div>
        <div className="vd-page-feature-grid">
          {DISCOVER_ITEMS.map((item) => <article key={item.title} className="vd-page-feature">
              <span className="vd-page-feature-icon" aria-hidden="true">
                <FontAwesomeIcon icon={item.icon} />
              </span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>)}
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-grid two">
          <article className="vd-page-card">
            <h2>Why It Matters to Property Buyers</h2>
            <p>Don’t make a property decision based on a brochure alone.</p>
            <p>
              Before paying a token advance or committing your savings, investigate the project, its registration
              and approval references, the land details, the developer’s claims, and the applicable legal requirements.
            </p>
            <p>
              VentureDirectory.com helps you begin that research journey by bringing available venture information
              into one searchable destination. Our platform is designed to support informed property discovery—
              not replace official verification, title due diligence, or independent legal advice.
            </p>
          </article>

          <article className="vd-page-card">
            <h2>Why Developers Should Join</h2>
            <p>
              In today’s digital-first property market, buyers and real estate professionals search online for
              venture names, registration numbers, approval references, locations, and developer details before making contact.
            </p>
            <ul className="vd-page-list">
              <li>Enhanced online visibility for venture information</li>
              <li>Organized project profiles in a structured format</li>
              <li>Reach buyers, investors, consultants, and property agents</li>
              <li>Featured promotional opportunities for selected projects</li>
              <li>Enquiry opportunities through available contact features</li>
              <li>A stronger digital brand beyond conventional advertisements</li>
            </ul>
          </article>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-section-head">
          <h2>Join the VentureDirectory Ecosystem</h2>
          <p>Be part of a more informed and connected property ecosystem.</p>
        </div>
        <div className="vd-page-audience-grid">
          {AUDIENCE.map((item) => <article key={item.title} className="vd-page-audience">
              <FontAwesomeIcon icon={faUserGroup} aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>)}
        </div>
        <div className="vd-page-cta-row">
          <Link to="/" className="vd-page-cta">Search Ventures</Link>
          <Link to="/how-it-works" className="vd-page-cta ghost">How It Works</Link>
          <Link to="/contact" className="vd-page-cta ghost">Contact Us</Link>
        </div>
      </section>

      <section className="vd-page-section">
        <div className="vd-page-notice">
          <h2>Our Commitment to Responsible Information</h2>
          <p>
            Real estate information must be handled carefully. Registration, approval, ownership, and legal status
            can vary from project to project and may change over time.
          </p>
          <p>
            VentureDirectory.com aims to organize and present available project information to support discovery and
            further investigation. A listing, registration reference, approval number, or promotional profile should
            not be interpreted as a guarantee of legal validity, ownership, construction quality, or investment returns.
          </p>
          <p>
            Users should independently confirm relevant details with the appropriate authorities, review current official
            records, examine title documents, and obtain qualified legal advice before committing funds.
          </p>
          <p className="vd-page-emphasis">Better information. Better questions. Better-informed property decisions.</p>
        </div>
      </section>
    </main>;
}

export default AboutPage;
